import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { CheckCircle2 } from 'lucide-react';
import { Card, CardBody, CardHeader } from '@/shared/components/ui/Card';
import { Input } from '@/shared/components/ui/Input';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { useReconciliation, useToggleCleared } from '../hooks/useReconciliation';
import { localToday } from '@/shared/utils/utils';

const money = (n: number) =>
  `${n < 0 ? '−' : ''}₹${Math.abs(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

function errorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const maybe = err as { response?: { data?: { error?: string } } };
    if (maybe.response?.data?.error) return maybe.response.data.error;
  }
  return fallback;
}

/**
 * Month-end bank reconciliation: tick the entries that have cleared the bank,
 * then prove the cleared balance against the statement closing balance. The
 * ledger itself is never modified — clearing marks are stored separately.
 */
export function BankReconciliation({ accountCode }: { accountCode: string }) {
  const [asOfDate, setAsOfDate] = useState(localToday());
  const [statementBalance, setStatementBalance] = useState('');
  const { data, isLoading, isError } = useReconciliation({ accountCode, asOfDate, statementBalance });
  const toggle = useToggleCleared();

  const reconciled = data?.difference === 0;

  return (
    <div className="space-y-4">
      {/* Controls + arithmetic */}
      <Card className="shadow-xs">
        <CardBody className="p-4 space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="w-44">
              <Input
                label="Reconcile up to"
                name="recon-date"
                type="date"
                value={asOfDate}
                onChange={(e) => setAsOfDate(e.target.value)}
              />
            </div>
            <div className="w-56">
              <Input
                label="Statement closing balance"
                name="recon-statement"
                type="number"
                step="0.01"
                placeholder="Enter the bank's figure"
                value={statementBalance}
                onChange={(e) => setStatementBalance(e.target.value)}
              />
            </div>
          </div>

          {data && (
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Balance per books</p>
                <p className="text-lg font-bold">{money(data.ledgerBalance)}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Cleared balance</p>
                <p className="text-lg font-bold">{money(data.clearedBalance)}</p>
                <p className="text-xs text-muted-foreground">{data.clearedCount} entries ticked</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Not yet cleared</p>
                <p className="text-lg font-bold">{money(data.unclearedTotal)}</p>
                <p className="text-xs text-muted-foreground">{data.unclearedCount} entries</p>
              </div>
              <div
                className={`rounded-lg border p-3 ${
                  data.difference === null
                    ? ''
                    : reconciled
                      ? 'border-emerald-300 bg-emerald-50'
                      : 'border-amber-300 bg-amber-50'
                }`}
              >
                <p className="text-xs text-muted-foreground">Difference</p>
                {data.difference === null ? (
                  <p className="text-sm text-muted-foreground mt-1">Enter the statement balance</p>
                ) : (
                  <>
                    <p className={`text-lg font-bold ${reconciled ? 'text-emerald-700' : 'text-amber-700'}`}>
                      {money(data.difference)}
                    </p>
                    <p className="text-xs">
                      {reconciled ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700">
                          <CheckCircle2 className="h-3 w-3" aria-hidden /> Reconciled
                        </span>
                      ) : (
                        <span className="text-amber-700">Statement − cleared</span>
                      )}
                    </p>
                  </>
                )}
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Entry list */}
      <Card className="shadow-xs">
        <CardHeader
          title="Ledger entries"
          subtitle="Tick each entry that appears on the bank statement"
        />
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-4"><Skeleton className="h-[300px]" /></div>
          ) : isError || !data ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              Could not load the reconciliation for this account.
            </div>
          ) : data.entries.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              No posted entries for this account up to the selected date.
            </div>
          ) : (
            <div className="max-h-[520px] overflow-auto">
              <table className="w-full text-sm text-left">
                <caption className="sr-only">Bank reconciliation entries</caption>
                <thead className="text-xs uppercase bg-muted/50 sticky top-0">
                  <tr>
                    <th scope="col" className="px-4 py-3 w-16">Cleared</th>
                    <th scope="col" className="px-4 py-3">Date</th>
                    <th scope="col" className="px-4 py-3">Narration</th>
                    <th scope="col" className="px-4 py-3">Type</th>
                    <th scope="col" className="px-4 py-3 text-right">Deposit</th>
                    <th scope="col" className="px-4 py-3 text-right">Withdrawal</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.entries.map((e) => (
                    <tr key={e.id} className={e.cleared ? 'bg-emerald-50/40' : 'hover:bg-muted/20'}>
                      <td className="px-4 py-2">
                        <input
                          type="checkbox"
                          aria-label={`Mark ${e.narration || e.voucherType} as cleared`}
                          checked={e.cleared}
                          disabled={toggle.isPending}
                          onChange={(ev) =>
                            toggle.mutate(
                              { ledgerEntryId: e.id, cleared: ev.target.checked, clearedOn: asOfDate },
                              { onError: (err) => toast.error(errorMessage(err, 'Could not update this entry.')) },
                            )
                          }
                        />
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {new Date(e.date).toLocaleDateString('en-IN')}
                      </td>
                      <td className="px-4 py-2">{e.narration || '—'}</td>
                      <td className="px-4 py-2 text-xs capitalize text-muted-foreground">
                        {e.voucherType?.replace(/_/g, ' ')}
                      </td>
                      <td className="px-4 py-2 text-right font-mono text-emerald-700">
                        {e.deposit ? money(e.deposit) : ''}
                      </td>
                      <td className="px-4 py-2 text-right font-mono text-red-600">
                        {e.withdrawal ? money(e.withdrawal) : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <p className="text-xs text-muted-foreground">
        Reconciliation never edits the ledger. Ticking an entry records that it cleared the bank; the
        accounting entries and the audit trail remain unchanged.
      </p>
    </div>
  );
}
